import SocialAuthButton from '@map/components/GuestArea/Authentication/shared/SocialAuthButton';
import TermsAppendix from '@map/components/GuestArea/Authentication/shared/TermsAppendix';
import LoginRegisterTransferLink from '@map/components/GuestArea/LoginRegisterTransferLink';
import { guestLinks } from '@map/components/GuestArea/links';
import useFeatureIsAccessible from '@map/hooks/useFeatureIsAccessible';
import { makeStyles } from '@map/theme';
import useAvailableProviders from '@shared/hooks/auth/useAvailableProviders';
import useChildrenSpacing from '@shared/hooks/useChildrenSpacing';
import { getConfigString } from '@shared/util/env';
import Button from '@ui/Button';
import FormDivider from '@ui/FormDivider';
import MailIcon from '@ui/Icon/library/MailIcon';
import NafathIcon from '@ui/Icon/library/NafathIcon';
import SmartphoneIcon from '@ui/Icon/library/SmartphoneIcon';
import NavButton from '@ui/NavButton';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import PublicPage from '../../shared/PublicPage';
import { useRegistrationContext } from '../context';

const useStyles = makeStyles(({ spacing, palette }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  footerContainer: {
    width: '100%',
    padding: spacing(0, 6),
  },
  customButton: {
    '&:hover': {
      color: palette.primary[500],
      backgroundColor: palette.primary[100],
      borderColor: palette.primary[500],
    },
    svg: {
      height: '24px',
      width: '24px',
    },
  },
}));

const Menu = () => {
  const { classes, cx } = useStyles();
  const availableProviders = useAvailableProviders();
  const phoneNumberAuth = useFeatureIsAccessible('phoneNumberAuth');
  const emailAuth = useFeatureIsAccessible('emailAuth');
  const isNefathAuthAccessible = useFeatureIsAccessible('nefathSignin');
  const { values } = useRegistrationContext();

  const standardMethods = useMemo(() => {
    const methods = [];
    if (emailAuth) {
      methods.push(
        <NavButton
          variant="secondary"
          fullWidth={true}
          size="medium"
          startIcon={<MailIcon />}
          to={guestLinks.registerEmail()}
          className={isNefathAuthAccessible ? classes.customButton : undefined}
        >
          <FormattedMessage id="registration.continue_with_email" />
        </NavButton>,
      );
    }
    if (phoneNumberAuth) {
      methods.push(
        <NavButton
          fullWidth={true}
          variant="secondary"
          size="medium"
          startIcon={<SmartphoneIcon />}
          to={guestLinks.registerPhone()}
          className={isNefathAuthAccessible ? classes.customButton : undefined}
        >
          <FormattedMessage id="registration.continue_with_phone" />
        </NavButton>,
      );
    }
    return methods;
  }, [classes.customButton, emailAuth, isNefathAuthAccessible, phoneNumberAuth]);

  return (
    <PublicPage
      innerClassName={cx(classes.root, useChildrenSpacing(2))}
      title={<FormattedMessage id="registration.create_account" />}
      footer={
        <div className={classes.footerContainer}>
          <LoginRegisterTransferLink to="login" />
        </div>
      }
    >
      {isNefathAuthAccessible ? (
        <form action="/users/auth/nefath" method="POST">
          {/* eslint-disable react/forbid-elements */}
          <input type="hidden" name="authenticity_token" value={getConfigString('csrf-token')} />
          <input type="hidden" name="role" value={values.role} />
          {/* eslint-enable react/forbid-elements */}
          <Button type="submit" startIcon={<NafathIcon />} fullWidth={true} size="medium">
            <FormattedMessage id="registration.continue_with_nefath" />
          </Button>
        </form>
      ) : (
        standardMethods
      )}
      {standardMethods.length > 0 && availableProviders.length > 0 && <FormDivider dense />}
      {isNefathAuthAccessible && standardMethods}
      {availableProviders.map((provider) => (
        <SocialAuthButton key={provider} provider={provider} label="signup_with" />
      ))}
      <TermsAppendix />
    </PublicPage>
  );
};

export default Menu;
